import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo, useState } from 'react';
import { TiLocationArrowOutline } from 'react-icons/ti';
import { MdFavoriteBorder, MdFavorite } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Flex,
  HStack,
  Icon,
  IconButton,
  Stack,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { ArrowBackIcon } from '@chakra-ui/icons';
import {
  EquipmentType,
  PlaygroundWrapper,
  RatingType,
} from '../../utils/types';
import Rating from '../Rating/Rating';
import axios from '../../api/axios';
import NewRating from '../Modal/NewRating';
import OpeningHours from '../OpeningHours/OpeningHours';
import Equipment from '../Equipment/Equipment';
import Score from '../Rating/Score';

import { useUser } from '../../context/UserContext';
import { ANONYMUS_USER_ID } from '../../utils/consts';
import NewEquipment from '../Modal/NewEquipment';

const PlaygroundDetail = () => {
  const { playgroundId } = useParams();
  const { t } = useTranslation();
  const [showAddNewRatingModal, setShowAddNewRatingModal] = useState(false);
  const [showAddNewEquipmentModal, setShowAddNewEquipmentModal] =
    useState(false);
  const { user } = useUser();
  const navigate = useNavigate();
  const toast = useToast();
  const userId = user?.id ? parseInt(user.id, 10) : ANONYMUS_USER_ID;
  const queryClient = useQueryClient();

  const playgroundDetailQuery = useQuery({
    queryKey: ['playgrounds'],
    queryFn: () =>
      axios({ url: `/playgrounds/${playgroundId}` }).then(
        (response) => response.data,
      ),
  });

  const userFavoritePlaygrounds = useQuery({
    queryKey: ['userFavoritePlaygrounds'],
    queryFn: async () => {
      const response = await axios.post(
        `/users/${user?.id}/favorite-playgrounds`,
        {
          id: userId,
          playgroundId,
        },
      );
      return response.data;
    },
  });

  const { data: playgroundDetailData, isLoading: playgroundDetailIsLoading } =
    playgroundDetailQuery;

  const { data: userFavoritePlaygroundsData } = userFavoritePlaygrounds;

  const onNewRatingClick = useCallback(() => {
    setShowAddNewRatingModal(!showAddNewRatingModal);
  }, [showAddNewRatingModal]);

  const onNewEquipmentClick = useCallback(() => {
    setShowAddNewEquipmentModal(!showAddNewEquipmentModal);
  }, [showAddNewEquipmentModal]);

  const isPlaygroundUserFavorite = useMemo(() => {
    if (!userFavoritePlaygroundsData || !playgroundDetailData) {
      return false;
    }
    const favoritePlaygroundIds = userFavoritePlaygroundsData.playgrounds.map(
      (playground: PlaygroundWrapper) => playground.playground.id,
    );
    return favoritePlaygroundIds.includes(playgroundDetailData.id);
  }, [playgroundDetailData, userFavoritePlaygroundsData]);

  const toggleFavoriteMutation = useMutation(
    async ({
      userId: mutationUserId,
      playgroundId: mutationPlaygroundId,
    }: {
      userId: number;
      playgroundId: string;
    }) => {
      const response = await axios.put(
        `/users/${mutationUserId}/favorite-playgrounds`,
        {
          userId: mutationUserId,
          playgroundId: mutationPlaygroundId,
        },
      );
      return response.data;
    },
    {
      onSuccess: () => {
        const notification = isPlaygroundUserFavorite
          ? t('playgrounds.notifications.remove_favorite_playground')
          : t('playgrounds.notifications.add_favorite_playground');
        toast({
          title: notification,
          status: 'success',
          duration: 9000,
          isClosable: true,
          position: 'top',
        });
        queryClient.invalidateQueries(['userFavoritePlaygrounds']);
      },
      onError: (error: any) => {
        toast({
          title: t('playgrounds.notifications.error_favorite_playground'),
          description: error.message,
          status: 'error',
          duration: 9000,
          isClosable: true,
          position: 'top',
        });
      },
    },
  );

  const onFavoriteToggleClick = useCallback(
    async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event.preventDefault();
      event.stopPropagation();

      if (userId !== ANONYMUS_USER_ID && playgroundId) {
        try {
          await toggleFavoriteMutation.mutateAsync({ userId, playgroundId });
        } catch (error: any) {
          toast({
            title: t('add_new_equipment_modal.errors.failed_add_equipment'),
            description: error.message,
            status: 'error',
            duration: 9000,
            isClosable: true,
            position: 'top',
          });
        }
      } else {
        toast({
          title: t('playgrounds.first_login_to_add_favorite'),
          status: 'error',
          duration: 9000,
          isClosable: true,
          position: 'top',
        });
      }
    },
    [userId, playgroundId, toggleFavoriteMutation, toast, t],
  );

  const handleGoBackClick = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const playgroundEquipments = useMemo(() => {
    if (!playgroundDetailData) {
      return null;
    }

    return playgroundDetailData?.equipments?.map(
      (playgroundEquipment: EquipmentType) => playgroundEquipment.equipment.id,
    );
  }, [playgroundDetailData]);

  return (
    <Flex>
      {playgroundDetailIsLoading && (
        <Flex
          justifyContent="center"
          alignItems="center"
          height="100vh"
          position="fixed"
          top="0"
          left="0"
          width="100%"
          background="rgba(255, 255, 255, 0.7)"
          zIndex="999"
        >
          <CircularProgress
            color="teal.500"
            size={12}
            thickness={10}
            isIndeterminate
          />
        </Flex>
      )}
      <Box flex="1" p="4">
        {playgroundDetailData && (
          <>
            <Button
              colorScheme="teal"
              variant="outline"
              size="sm"
              leftIcon={<ArrowBackIcon />}
              onClick={handleGoBackClick}
            >
              {t('button.back')}
            </Button>
            <Stack direction="row" justifyContent="space-between">
              <Text
                display="flex"
                alignItems="center"
                color="#1B4965"
                fontSize="20px"
                fontWeight={700}
              >
                <Icon
                  as={TiLocationArrowOutline}
                  w={6}
                  h={6}
                  color="teal.500"
                  mr={1}
                />
                {playgroundDetailData.name}
              </Text>
              {user && (
                <Tooltip
                  label={
                    isPlaygroundUserFavorite
                      ? String(t('tooltip.remove_from_favorite'))
                      : String(t('tooltip.add_to_favorite'))
                  }
                  aria-label={String(t('tooltip.add_to_favorite'))}
                >
                  <IconButton
                    size="lg"
                    aria-label="favorite"
                    colorScheme="teal"
                    variant={isPlaygroundUserFavorite ? 'solid' : 'outline'}
                    fontSize="25px"
                    icon={
                      isPlaygroundUserFavorite ? (
                        <MdFavorite />
                      ) : (
                        <MdFavoriteBorder />
                      )
                    }
                    onClick={onFavoriteToggleClick}
                  />
                </Tooltip>
              )}
            </Stack>
            <Text color="#1B4965" fontSize="16px" fontWeight={400} ml={7}>
              {playgroundDetailData.address}
            </Text>
            <OpeningHours openingHours={playgroundDetailData.openingHours} />
            <HStack my={1} spacing={2}>
              <Score averageScore={playgroundDetailData.averageRating} />
              {playgroundDetailData.totalReviews && (
                <Text>{`${playgroundDetailData.totalReviews} ${t(
                  'playgrounds.reviews',
                )}`}</Text>
              )}
            </HStack>
            {playgroundDetailData?.equipments &&
              Array.isArray(playgroundDetailData.equipments) && (
                <>
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="space-between"
                    mt={6}
                    mb={2}
                  >
                    <Text color="#1B4965" fontSize="18px" fontWeight={700}>
                      {t('playgrounds.equipments')}
                    </Text>
                    <Button onClick={onNewEquipmentClick} colorScheme="teal">
                      {t('button.add_equipment')}
                    </Button>
                  </Stack>
                  <Equipment equipments={playgroundDetailData.equipments} />
                </>
              )}
          </>
        )}
      </Box>
      <Box flex="1" p="4">
        <Stack direction="row" justifyContent="space-between">
          <Text
            display="flex"
            alignItems="center"
            color="#1B4965"
            fontSize="20px"
            fontWeight={700}
          >
            {playgroundDetailData?.ratings?.length
              ? t('playgrounds.number_of_ratings', {
                  sumOfComment: playgroundDetailData?.ratings?.length,
                })
              : t('playgrounds.no_rating')}
          </Text>
          <Button onClick={onNewRatingClick} colorScheme="teal" variant="solid">
            {t('button.add_rating')}
          </Button>
        </Stack>
        <Box>
          {playgroundDetailData?.ratings?.map((rating: RatingType) => {
            return (
              <Rating
                key={rating.comment}
                user={rating.user}
                score={rating.score}
                comment={rating.comment}
                playgroundName={playgroundDetailData.name}
                playgroundId={+playgroundId!}
                createdAt={rating.createdAt}
                updatedAt={rating.updatedAt}
              />
            );
          })}
        </Box>
      </Box>
      {showAddNewRatingModal && (
        <NewRating
          isOpen
          onClose={onNewRatingClick}
          userId={userId}
          playgroundId={playgroundId}
        />
      )}
      {showAddNewEquipmentModal && (
        <NewEquipment
          isOpen
          onClose={onNewEquipmentClick}
          playgroundId={playgroundId}
          selectedEquipment={playgroundEquipments}
        />
      )}
    </Flex>
  );
};

export default PlaygroundDetail;
